import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../core/services/api.service';
import { AuthService } from '../../core/services/auth.service';
import { Client } from '../../shared/models/models';

const PAGE_SIZE = 25;

@Component({
  selector: 'app-clients',
  standalone: false,
  template: `
    <div class="page-wrapper">
      <header class="top-bar">
        <div class="top-bar-content">
          <img src="assets/logo.png" alt="Banner Audit" class="logo" />
          <span class="app-title">Banner Audit</span>
          <span class="spacer"></span>
          <span class="user-email">{{ userEmail }}</span>
          <button mat-icon-button (click)="logout()" matTooltip="Cerrar sesión">
            <mat-icon>logout</mat-icon>
          </button>
        </div>
      </header>

      <main class="page-content">
        <div class="page-header">
          <h1>Clientes</h1>
          <p>Seleccioná un cliente para ver sus diseños y el historial de cambios.</p>
        </div>

        <mat-form-field appearance="outline" class="search-field">
          <mat-label>Buscar por nombre o ID</mat-label>
          <mat-icon matPrefix>search</mat-icon>
          <input matInput
                 [value]="searchInput"
                 (input)="onSearch($any($event.target).value)"
                 placeholder="Nombre o número de ID..." />
          @if (searchInput) {
            <button matSuffix mat-icon-button (click)="clearSearch()">
              <mat-icon>close</mat-icon>
            </button>
          }
        </mat-form-field>

        @if (loading()) {
          <div class="cards-grid">
            @for (i of skeletons; track i) {
              <div class="client-card skeleton"></div>
            }
          </div>
        } @else if (clients().length === 0) {
          <div class="empty-state">
            <mat-icon>person_search</mat-icon>
            <p>No se encontraron clientes.</p>
          </div>
        } @else {
          <div class="cards-grid">
            @for (client of clients(); track client.id) {
              <div class="client-card" (click)="goToBanners(client)" role="button" tabindex="0"
                   (keydown.enter)="goToBanners(client)">
                <div class="client-avatar">{{ initials(client.name) }}</div>
                <div class="client-info">
                  <span class="client-name">{{ client.name }}</span>
                  <span class="client-id">ID {{ client.id }}</span>
                </div>
                <mat-icon class="arrow">chevron_right</mat-icon>
              </div>
            }
          </div>

          @if (totalElements() > PAGE_SIZE) {
            <mat-paginator
              [length]="totalElements()"
              [pageSize]="PAGE_SIZE"
              [pageIndex]="page()"
              [hidePageSize]="true"
              (page)="onPageChange($event.pageIndex)">
            </mat-paginator>
          }
        }
      </main>
    </div>
  `,
  styleUrls: ['./clients.component.scss']
})
export class ClientsComponent implements OnInit, OnDestroy {
  private api = inject(ApiService);
  private auth = inject(AuthService);
  private router = inject(Router);

  clients = signal<Client[]>([]);
  totalElements = signal(0);
  page = signal(0);
  loading = signal(true);
  skeletons = Array(6);
  readonly PAGE_SIZE = PAGE_SIZE;

  searchInput = '';
  private search$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  get userEmail() { return this.auth.getUserEmail(); }

  ngOnInit(): void {
    this.search$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(q => {
        this.page.set(0);
        this.loading.set(true);
        return this.api.getClients(0, q);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: r => { this.clients.set(r.content); this.totalElements.set(r.total_elements); this.loading.set(false); },
      error: () => this.loading.set(false),
    });

    this.search$.next('');
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  onSearch(value: string): void {
    this.searchInput = value;
    this.search$.next(value);
  }

  clearSearch(): void { this.onSearch(''); }

  onPageChange(pageIndex: number): void {
    this.page.set(pageIndex);
    this.loading.set(true);
    this.api.getClients(pageIndex, this.searchInput).subscribe({
      next: r => { this.clients.set(r.content); this.totalElements.set(r.total_elements); this.loading.set(false); },
      error: () => this.loading.set(false),
    });
  }

  initials(name: string): string {
    return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
  }

  goToBanners(client: Client): void {
    this.router.navigate(['/clients', client.id, 'banners'], { state: { clientName: client.name } });
  }

  logout(): void { this.auth.logout(); }
}
